import { CustomReport } from "../models/CustomReport";
import { CustomReportField } from "../models/CustomReportField";
import { CustomReportFieldType } from "../models/CustomReportFieldType";
import { CustomReportSchema } from "../models/CustomReportSchema";
import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { IQueueExecutionRequest } from "@/shared/Executions/models/IQueueExecutionRequest";
import { CustomReportsTypeGuardService } from "./CustomReportsTypeGuardService";
import { QueueCustomReportsArgs } from "@/shared/Executions/models/QueueCustomReportsArgs";
import { QueueExecutionRequestFactory } from "@/shared/Executions/services/QueueExecutionRequestFactory";
import { TemplateSelection } from "@/components/TemplateManagement/models";

export class CustomReportsService {

    public static readonly CUSTOM_CUSTOMREPORT_TYPE = "unknown";

    public static getFlattenedCustomReport(customReport: CustomReport, includeType: string | null): CustomReport {
        if (customReport == null) throw new Error("customReport is required");

        const customReportObject = customReport.configuration ?? customReport;
        const { _type, ...properties } = customReportObject || {};
        const flattenedCustomReport = { ...properties } as CustomReport;
        if (includeType != null) {
            flattenedCustomReport.type = includeType;
        }
        return flattenedCustomReport;
    }

    public static createCustomReportFromCustomReportSchema(customReportSchema: CustomReportSchema): CustomReport {
        const newCustomReport: CustomReport = {
            id: this.generateCustomReportId(),
            _type: customReportSchema.type,
            display_name: customReportSchema.display_name,
            description: 'New Report configuration',
            category: customReportSchema.category,
            configuration: {},
            schema: customReportSchema
        };
        return newCustomReport;
    }

    public static createCustomReportFromCustomReportField(customReportField: CustomReportField): CustomReport {
        const newCustomReport: CustomReport = {
            id: this.generateCustomReportId(),
            _type: customReportField.field_type,
            display_name: customReportField.display_name,
            display_field: customReportField.display_field,
            description: customReportField.description,
            category: "Default Value",
            configuration: {},
            schema: customReportField
        };
        return newCustomReport;
    }

    private static generateCustomReportId(): string {
        const id = `customreport-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        return id;
    }

    public static initializeCustomReportConfiguration(customReport: CustomReport, fields: Array<CustomReportField>): Record<string, any> {
        const config: Record<string, any> = {};
        config.description = `${customReport.display_name ?? "CustomReport"} Description`;
        fields.forEach((field: CustomReportField) => {
            this.initializeCustomReportConfigurationForField(field, config);
        });
        config._type = customReport._type;
        return config;
    }

    public static initializeCustomReportFieldConfiguration(fields: Array<CustomReportField>): Record<string, any> {
        const config: Record<string, any> = {};
        fields.forEach((field: CustomReportField) => {
            this.initializeCustomReportConfigurationForField(field, config);
        });
        return config;
    }

    public static initializeCustomReportConfigurationForField(field: CustomReportField, configuration?: Record<string, any>): Record<string, any> {
        const config: Record<string, any> = configuration || {};
        if (field.default_value !== undefined) {
            config[field.name] = field.default_value;
        } else if (field.field_type === CustomReportFieldType.Boolean) {
            config[field.name] = false;
        } else if (
            field.field_type === CustomReportFieldType.MultiSelect ||
            field.field_type === CustomReportFieldType.Array
        ) {
            config[field.name] = [];
        } else if (
            field.field_type === CustomReportFieldType.Object
        ) {
            config[field.name] = {};
        } else {
            config[field.name] = '';
        }
        return config;
    }

    public static createQueueCustomReportsArgs(
        parentExecutionId: string,
        flattenedCustomReports: Array<CustomReport>,
        name?: string,
        templateSelection?: TemplateSelection | null): QueueCustomReportsArgs {

        const args = new QueueCustomReportsArgs(
            flattenedCustomReports,
            parentExecutionId,
            name,
            templateSelection,
        )

        return args
    }

    public static getFormattedCustomReportsJson(parentExecutionId: string, executionName: string | undefined, customReports: Array<CustomReport>): IQueueExecutionRequest {
        const flattenedCustomReports = this.getFlattenedCustomReports(customReports);
        const args = CustomReportsService.createQueueCustomReportsArgs(parentExecutionId, flattenedCustomReports, executionName);
        const queueExecutionArgs = new QueueExecutionArgs({ tenantId: '', userId: '', customReportArgs: args });
        const payload = QueueExecutionRequestFactory.createQueueExecutionRequest(queueExecutionArgs);
        return payload;
    }

    public static getFlattenedCustomReports(customReports: Array<CustomReport>): Array<CustomReport> {
        const flattenedCustomReports = (customReports || []).map((x) => CustomReportsService.getFlattenedCustomReport(x, x._type));
        this.recursivelyFlattenCustomReports(flattenedCustomReports);
        return flattenedCustomReports;
    }

    private static recursivelyFlattenCustomReports(customReports: Array<CustomReport | Object>, visited = new Set<any>()) {
        for (const customReport of customReports) {
            if (visited.has(customReport)) continue;
            visited.add(customReport);

            for (const key of Object.keys(customReport)) {
                const value = (customReport as any)[key];

                if (Array.isArray(value)) {
                    const containsSubCustomReports =
                        value.length > 0 &&
                        value.every((item) => item && CustomReportsTypeGuardService.isCustomReport(item));

                    if (containsSubCustomReports) {
                        const flattenedSubCustomReports = value.map((subCustomReport: CustomReport) => {
                            const includeType = !this.isInternalSchemaType(subCustomReport._type) ? subCustomReport._type : null;
                            return this.getFlattenedCustomReport(subCustomReport, includeType);
                        });

                        (customReport as any)[key] = flattenedSubCustomReports;
                        this.recursivelyFlattenCustomReports(flattenedSubCustomReports, visited);
                    }
                }
                else if (CustomReportsTypeGuardService.isNonEmptyCustomReportObject(value)) {

                    for (const nestedKey of Object.keys(value)) {
                        const nestedValue = value[nestedKey];

                        // Case 1: Direct customReport object
                        if (CustomReportsTypeGuardService.isCustomReport(nestedValue)) {
                            const includeType = !this.isInternalSchemaType(nestedValue._type) ? nestedValue._type : null;
                            const flattened = this.getFlattenedCustomReport(nestedValue, includeType);
                            value[nestedKey] = flattened;

                            this.recursivelyFlattenCustomReports([flattened], visited);
                        }

                        // Case 2: Another nested non-empty customReport-like object
                        else if (CustomReportsTypeGuardService.isNonEmptyCustomReportObject(nestedValue)) {
                            const coerced = nestedValue as Object;
                            this.recursivelyFlattenCustomReports([coerced], visited);
                        }

                        else if (Array.isArray(nestedValue)) {
                            const customReports = this.extractCustomReportsFromArray(nestedValue);
                            if (customReports.length > 0) {
                                const flattened = customReports.map(subCustomReport => {
                                    const includeType = !this.isInternalSchemaType(subCustomReport._type) ? subCustomReport._type : null;
                                    return this.getFlattenedCustomReport(subCustomReport, includeType);
                                });
                                value[nestedKey] = flattened;
                                this.recursivelyFlattenCustomReports(flattened, visited);
                            }
                        }
                    }
                }

            }
        }
    }

    private static extractCustomReportsFromArray(arr: unknown[]): CustomReport[] {
        return arr.filter(CustomReportsTypeGuardService.isCustomReport);
    }

    public static getFormattedCustomReportsJsonString(parentExecutionId: string, executionName: string | undefined, customReports: Array<CustomReport>, customReportsOnly: boolean = false): string {
        const payload = CustomReportsService.getFormattedCustomReportsJson(parentExecutionId, executionName, customReports);
        return JSON.stringify((customReportsOnly ? payload.customReports : payload), null, 2);
    }

    public static tryGetCustomReportSchemaByType(type: string, customReportSchemas: Array<CustomReportSchema>): CustomReportSchema | undefined {
        if (type == null) throw new Error("type is required");
        if (customReportSchemas == null) throw new Error("customReportSchemas are required");
        return customReportSchemas.find(x => x.type === type) || customReportSchemas.find(x => x.type === CustomReportsService.CUSTOM_CUSTOMREPORT_TYPE);
    }

    // If you have internal schema types for customReports, implement this
    public static isInternalSchemaType(type: string): boolean {
        const internalTypes = ['object', 'array'];
        return internalTypes.indexOf(type?.toLowerCase().trim()) !== -1;
    }


}
